import { OAuthProviders, ProviderBoxesView } from '@linearb/linui-common';
import AppRouter from 'common/AppRouter';
import { PRIVACY_POLICY_LINK, TERMS_LINK } from 'common/constants/constants';
import {
  removeIsGdprCookie,
  storeIsGdprCookie
} from 'common/services/gdpr.service';
import { Flex } from 'components/base/layout-components';
import Text from 'components/base/Text';
import InfoBox from 'components/InfoBox';
import { signUp } from 'modals/AuthModals/auth.modals.service';
import { useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  StyledBoxWrapper,
  StyledContentHeader,
  StyledLoginWrapper,
  StyledSubHeader,
  StyledText
} from './RegisterContent.style';
import { validate as validateUUID } from 'uuid';
import { useLocation } from 'react-router';
import { License } from './RegisterV2';
import { Link } from '@linearb/linui-common/marketing/Link';
import { Checkbox } from '@linearb/linui-common/marketing/Checkbox';
import { Button } from '@linearb/linui-common/marketing/Button';

/**
 * Initiates sign up request
 */
const handleSignUp = (provider: Record<string, unknown>, license: License) => {
  const queryParams = {
    license
  };

  signUp(
    `${provider.authIdentity}_identity`,
    window.location.origin + AppRouter.PERSONAL_IDENTIFICATION_FORM.path,
    false,
    queryParams
  );
};

interface RegisterContentProps {
  license: License;
}

const RegisterContent = ({ license }: RegisterContentProps) => {
  const [isGdprConsentGiven, setIsGdprConsentGiven] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);
  const email = urlParams.get('email');
  const organizationName = urlParams.get('organization_name');
  const isMidSignUp = useMemo(
    () => validateUUID(organizationName),
    [organizationName]
  );

  const handleGdprConsentChange = event => {
    const newValue = event.target.checked;
    setIsGdprConsentGiven(newValue);
    newValue ? storeIsGdprCookie() : removeIsGdprCookie();
  };

  return (
    <Flex direction='column'>
      <StyledContentHeader>Welcome to LinearB</StyledContentHeader>
      {email && (
        <StyledBoxWrapper>
          {/* @ts-expect-error */}
          <InfoBox
            iconAlign='center'
            iconSize='normal'
            iconColor='orangePeel'
            borderAlpha={0.2}
            backgroundColor='orangePeel'
            backgroundAlpha={0.1}
            borderColor='orangePeel'
          >
            <Text size='medium'>
              {isMidSignUp ? (
                <span data-testid='warning-sign-mid-registration'>
                  The email <strong>{email}</strong> is already in the process
                  of sign-up. Please login to continue the signup process, or
                  you can register with a different email.
                </span>
              ) : (
                <span data-testid='warning-signup-with-existing-account'>
                  The email <strong>{email}</strong> is already in use. You can
                  register with another email or login{' '}
                  {organizationName ? `to ${organizationName}` : null}.
                </span>
              )}
            </Text>
          </InfoBox>
        </StyledBoxWrapper>
      )}
      <StyledSubHeader>Register with:</StyledSubHeader>
      <Flex justify='center' mt='-1rem' mb='2rem'>
        <ProviderBoxesView
          disabled={false}
          providers={OAuthProviders}
          onProviderClick={setSelectedProvider}
          lastLoggedInProviderName={selectedProvider?.title}
          showTooltip={false}
        />
      </Flex>
      <Checkbox
        checked={isGdprConsentGiven}
        onChange={handleGdprConsentChange}
        sx={{ marginX: '5rem', marginBottom: '30px' }}
        label={
          <>
            I Agree to LinearB's Storage And Processing Of My Personal Data And
            To The{' '}
            <Link
              hideChevron
              sx={{ display: 'inline', fontSize: '1rem' }}
              href={TERMS_LINK}
              target='_blank'
              rel='noreferrer'
            >
              Terms Of Service
            </Link>{' '}
            And{' '}
            <Link
              hideChevron
              sx={{ display: 'inline', fontSize: '1rem' }}
              href={PRIVACY_POLICY_LINK}
              target='_blank'
              rel='noreferrer'
            >
              Privacy Policy
            </Link>
          </>
        }
      />
      <Flex justify='center' items='center' mt='1.5rem'>
        <Button
          disabled={!selectedProvider || !isGdprConsentGiven}
          onClick={() => handleSignUp(selectedProvider, license)}
        >
          Create an account
        </Button>
      </Flex>
      <StyledLoginWrapper>
        <StyledText>Already a user?</StyledText>
        <Button
          component={RouterLink}
          replace
          to='/login'
          variant='outline'
          size='sm'
        >
          {organizationName && !isMidSignUp
            ? `Login to ${organizationName}`
            : 'Login'}
        </Button>
      </StyledLoginWrapper>
    </Flex>
  );
};

export default RegisterContent;
